---
layout: docs
title: "Assistants & Sub-Assistants"
---


Corral uses a two-level model for AI agents: assistants and sub-assistants.

---

## Assistants

An assistant is the top-level AI entity. Every user gets a **Core Assistant** when they first access Corral. An assistant has:

- **A personality** — defined by a system prompt that shapes how it communicates and reasons
- **A persistent file system** — files in the `/workspace/` scope survive across conversations
- **Conversation history** — past conversations are accessible and continuable
- **Tool access** — built-in tools (file system, code execution, rich content) plus any MCP and OpenAPI integrations configured by admins
- **Awareness of sub-assistants** — the assistant knows about and can interact with its sub-assistants

The Core Assistant is a general-purpose AI. It's configured with a broad system prompt and full tool access, ready to help with anything.

---

## Sub-Assistants

A sub-assistant is a purpose-built workspace for domain-specific work. Each sub-assistant has:

- **Its own assistant** — with a system prompt tailored to the domain
- **Its own file system** — separate workspace storage from the parent
- **Its own conversation state** — isolated context
- **Configurable tools** — potentially different tool access than the parent assistant

### When to Use Sub-Assistants

Sub-assistants are designed for focused, ongoing work in a specific domain:

- **Marketing** — a sub-assistant with marketing-specific context, tools connected to campaign platforms, and a system prompt that understands brand guidelines
- **Engineering** — a sub-assistant with code execution, repository integrations, and technical context
- **Research** — a sub-assistant with web search tools, document analysis, and accumulated research files
- **Customer Support** — a sub-assistant with access to support systems, knowledge bases, and escalation workflows

Each sub-assistant accumulates context and files relevant to its domain over time, becoming more useful as it's used.

### Creating Sub-Assistants

Sub-assistants can be created in two ways:

1. **From the Hub** — users can create sub-assistants directly from the chat interface
2. **By the assistant itself** — the Core Assistant has a `create_sub_assistant` tool and can create domain-specific sub-assistants on request

When created, the sub-assistant gets an isolated workspace with separate files and conversation state. The parent assistant maintains awareness of its sub-assistants.

---

## How They Relate to Apps

In the admin console, both assistants and sub-assistants are **apps** — configured agent instances with intelligence settings, tools, channels, permissions, and versions. The distinction between "assistant" and "sub-assistant" is a user-facing concept in the Hub; the underlying infrastructure is the same.

This means admins can configure sub-assistant templates with specific tool sets, system prompts, and permissions, then make them available for users to instantiate.

---

## Multi-Agent Composition

Beyond the assistant/sub-assistant model, Corral supports multi-agent composition at the platform level:

- **Agent Plugins** — configured sub-agents can be wrapped as tools and invoked by a parent agent. The parent calls the sub-agent like any other tool, passing context and receiving results.
- **Sub-Agent Isolation** — each sub-agent runs in its own scope, with its own tool set and conversation state. Resources are cleaned up when the parent finishes.

This enables complex workflows where a primary agent delegates specialized tasks to purpose-built sub-agents.
